// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file MultiSelector.tsx
 * @input Uses React, StyleX, usePopover, useTooltip, CheckboxInput, Field, Badge, Icon, InputGroupContext
 * @output Exports MultiSelector component
 * @position Core implementation; consumed by index.ts
 *
 * SYNC: When modified, update:
 * - /packages/core/src/MultiSelector/MultiSelector.doc.mjs
 * - /packages/core/src/MultiSelector/MultiSelector.test.tsx
 * - /packages/core/src/MultiSelector/index.ts
 * - /apps/storybook/stories/InputGroup.stories.tsx
 * - /packages/cli/assets/templates/blocks/components/MultiSelector/ (showcase blocks)
 */
import React, { useCallback, useEffect, useId, useMemo, useOptimistic, useRef, useState, useTransition } from 'react';
import * as stylex from '@stylexjs/stylex';
import { usePopover } from "../Popover/usePopover.js";
import { useTooltip } from "../Tooltip/index.js";
import { Icon, renderIconSlot } from "../Icon/index.js";
import { Field, inputStatusBorderStyles, inputStatusHoverShadowStyles, inputWrapperStyles } from "../Field/index.js";
import { Divider } from "../Divider/index.js";
import { Spinner } from "../Spinner/index.js";
import { TextInput } from "../TextInput/index.js";
import { CheckboxInput } from "../CheckboxInput/index.js";
import { Badge } from "../Badge/index.js";
import "../theme/tokens.stylex.js";
import { colorVars, sizeVars, spacingVars, radiusVars, durationVars, easeVars, typographyVars, fontWeightVars, typeScaleVars, borderVars } from "../theme/tokens.stylex.js";
import { isOptionData, isDivider, isSection, normalizeOption, getSelectableOptions } from "../Selector/utils.js";
import { useMultiCombobox } from "./hooks.js";
import { getInputARIA, mergeProps } from "../utils/index.js";
import { useAnnounce } from "../hooks/useAnnounce.js";
import { useSize } from "../SizeContext/SizeContext.js";
import { themeProps } from "../utils/themeProps.js";
import { groupStyles } from "../InputGroup/groupStyles.js";
import { useInputGroup } from "../InputGroup/InputGroupContext.js";
import { VisuallyHidden } from "../VisuallyHidden/index.js";
import { useTranslator } from "../i18n/index.js";

// Sentinel value for the select-all item in keyboard navigation
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
const SELECT_ALL_VALUE = '__xds_select_all__';
const styles = {
  triggerContainer: {
    kVAEAm: "x1n2onr6",
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kjj79g: "x1qughib",
    kOIVth: "x1txdalj",
    kzqmXN: "xh8yej3",
    k8WAf4: "xce4md1",
    kg3NbH: "xrrkdod",
    kMv6JI: "x9ynric",
    kGuDYH: "xcr08ib",
    kLWn49: "x1kq96og",
    kMwMTN: "x1tgivj0",
    kkrTdU: "x1ypdohk",
    $$css: true
  },
  triggerPlaceholder: {
    kMwMTN: "xv1l7n4",
    $$css: true
  },
  triggerGhost: {
    kzqmXN: "x14atkfc",
    kMzoRj: "xc342km",
    kWkggS: "xjbqb8w",
    kKwaWg: "x1ilzqfv xq8i9tn",
    kGVxlE: "x1gnnqk1 x1irk71m x15ysqaf",
    k63SB2: "x1e4wzip",
    kI3sdo: "x1a2a7pz x1irc7jg",
    kInvED: "x1wfwxd8 xscfbxq",
    k1ekBW: "xrafxwg",
    k3aq6I: "x3oybdh xk4oym4",
    $$css: true
  },
  triggerGhostDisabled: {
    kKwaWg: "x18o3ruo",
    k3aq6I: "x1c071of x1pdlv7q",
    $$css: true
  },
  popover: {
    k7Eaqz: "xrzjruh",
    keoZOQ: "xcsaf9d",
    $$css: true
  },
  selectAllWrapper: {
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kOIVth: "x1txdalj",
    kkrTdU: "x1ypdohk",
    $$css: true
  },
  sectionDivider: {
    kqGvvJ: "xsq74q5",
    $$css: true
  },
  divider: {
    kqGvvJ: "xsq74q5",
    $$css: true
  },
  item: {
    kB7OPa: "x9f619",
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kOIVth: "x1txdalj",
    kzqmXN: "xh8yej3",
    kaIpWk: "xh6dtrn",
    kkrTdU: "x1ypdohk",
    kWkggS: "xjbqb8w",
    kI3sdo: "x1a2a7pz",
    $$css: true
  },
  itemHighlighted: {
    kWkggS: "x1lmrjuc",
    $$css: true
  },
  itemDisabled: {
    kSiTet: "xbyyjgo",
    kkrTdU: "x1h6gzvc",
    $$css: true
  }
};
const sizeStyles = {
  sm: {
    kZKoxP: "x6k0iem",
    $$css: true
  },
  md: {
    kZKoxP: "x1ueg155",
    $$css: true
  },
  lg: {
    kZKoxP: "xssyfek",
    $$css: true
  }
};
const itemSizeStyles = {
  sm: {
    kmVPX3: "x9epnlk",
    $$css: true
  },
  md: {
    k8WAf4: "x1vofgu7",
    kg3NbH: "xf314gf",
    $$css: true
  },
  lg: {
    kmVPX3: "xlsj2fj",
    $$css: true
  }
};
const selectAllSizeStyles = {
  sm: {
    kg3NbH: "x7a5moj",
    k8WAf4: "x13f7esw",
    $$css: true
  },
  md: {
    kg3NbH: "xf314gf",
    k8WAf4: "xu0wf1k",
    $$css: true
  },
  lg: {
    kg3NbH: "xf314gf",
    k8WAf4: "xu0wf1k",
    $$css: true
  }
};
const STATUS_ICON_MAP = {
  warning: 'warning',
  error: 'error',
  success: 'success'
};
const STATUS_ICON_COLOR_MAP = {
  warning: 'warning',
  error: 'error',
  success: 'success'
};
const STATUS_BUTTON_LABEL_KEY = {
  warning: '@astryx.input.statusButton.warning',
  error: '@astryx.input.statusButton.error',
  success: '@astryx.input.statusButton.success'
};
// Case-insensitive substring match for a single option. The one predicate used
// by both the flat filter (count + keyboard nav) and the grouped renderer, so
// what is shown while searching stays in lockstep with the announced count.
function optionMatchesQuery(option, query) {
  if (!query) {
    return true;
  }
  return (option.label ?? option.value).toLowerCase().includes(query.toLowerCase());
}

// Case-insensitive substring filter over the selectable options. Shared by the
// `filteredItems` memo (rendering) and the search-change handler, which needs
// the count for the *next* query synchronously to announce it exactly once per
// keystroke rather than reacting to state in an effect.
function filterOptionsByQuery(items, query) {
  if (!query) {
    return items;
  }
  return items.filter(item => optionMatchesQuery(item, query));
}

/**
 * A multi-select dropdown component with checkboxes for choosing
 * multiple items from a list of options.
 *
 * @example
 * ```
 * <MultiSelector
 *   label="Columns"
 *   options={['Name', 'Email', 'Role', 'Status']}
 *   value={selectedColumns}
 *   onChange={setSelectedColumns}
 *   hasSelectAll
 * />
 * ```
 */
export function MultiSelector({
  label,
  isLabelHidden = false,
  description,
  isOptional = false,
  isRequired = false,
  isDisabled = false,
  disabledMessage,
  options,
  value,
  onChange,
  changeAction,
  isLoading = false,
  placeholder: placeholderFromProps,
  size: sizeProp,
  variant = 'input',
  status,
  statusVariant = 'attached',
  labelTooltip,
  startIcon,
  hasClear = false,
  hasSelectAll = false,
  selectAllLabel: selectAllLabelFromProps,
  hasSearch = false,
  searchPlaceholder: searchPlaceholderFromProps,
  triggerDisplay = 'count',
  maxBadges = 3,
  renderOption,
  isDefaultOpen = false,
  'data-testid': testId,
  htmlName,
  width,
  xstyle,
  className,
  style
}) {
  const t = useTranslator();
  const placeholder = placeholderFromProps ?? t('@astryx.multiSelector.selectPlaceholder');
  const selectAllLabel = selectAllLabelFromProps ?? t('@astryx.multiSelector.selectAll');
  const searchPlaceholder = searchPlaceholderFromProps ?? t('@astryx.multiSelector.searchPlaceholder');
  const size = useSize(sizeProp, 'md');
  const effectiveStatusVariant = variant === 'ghost' && statusVariant === 'attached' ? 'detached' : statusVariant;
  const triggerId = useId();
  const listboxId = useId();
  const descriptionId = useId();
  const statusMessageId = useId();
  const inputLabelId = useId();
  const searchId = useId();
  const triggerRef = useRef(null);
  const searchRef = useRef(null);
  const inputGroup = useInputGroup();
  const [searchQuery, setSearchQuery] = useState('');
  // A typed query shows TextInput's built-in clear (✕) button, which becomes
  // the next tab stop after the search input.
  const hasQuery = searchQuery.length > 0;

  // Snapshot of which values were selected when the dropdown opened.
  // Stored as state (not a ref) so sortedItems recomputes exactly once on open,
  // then stays frozen until the menu closes.
  const [selectedAtOpen, setSelectedAtOpen] = useState(null);
  const [, startTransition] = useTransition();
  const [optimisticValue, setOptimisticValue] = useOptimistic(value);
  const isBusy = isLoading || optimisticValue !== value;

  // Disabled-reason tooltip. Disabled controls swallow pointer events, so the
  // tooltip listeners attach to the trigger container (which already exists)
  // and the trigger button stays perceivable via aria-disabled instead of the
  // disabled attribute. Activation is blocked by the isDisabled guards in
  // useMultiCombobox (onTriggerClick / onKeyDown).
  const showsDisabledMessage = isDisabled && !!disabledMessage;
  const disabledMessageTooltip = useTooltip({
    placement: 'above',
    // The container div is not naturally focusable; focusin bubbles up from
    // the trigger button, so always attach focus listeners.
    focusTrigger: 'always',
    isEnabled: showsDisabledMessage
  });
  const statusTooltip = useTooltip({
    placement: 'above',
    isEnabled: effectiveStatusVariant === 'tooltip' && !!status?.message
  });
  const {
    ariaLabelledBy,
    ariaDescribedBy
  } = getInputARIA(inputLabelId, [description ? descriptionId : null, !inputGroup && effectiveStatusVariant !== 'tooltip' && status?.message ? statusMessageId : null, effectiveStatusVariant === 'tooltip' && status?.message ? statusTooltip.describedBy : null, showsDisabledMessage ? disabledMessageTooltip.describedBy : null], inputGroup);

  // Flatten options for keyboard navigation
  const selectableItems = useMemo(() => getSelectableOptions(options), [options]);

  // Announce selection-count changes politely (comboboxes-7 announce path).
  // Toggling options / select-all previously produced no audible feedback.
  const announce = useAnnounce();
  const announceSelection = useCallback(nextValue => {
    const total = selectableItems.length;
    const selectableSet = new Set(selectableItems.map(item => item.value));
    const selectedCount = nextValue.filter(v => selectableSet.has(v)).length;
    if (selectedCount === 0) {
      announce('Selection cleared');
    } else if (total > 0 && selectedCount === total) {
      announce('All selected');
    } else {
      announce(`${selectedCount} of ${total} selected`);
    }
  }, [announce, selectableItems]);

  // Filter items by search query
  const filteredItems = useMemo(() => filterOptionsByQuery(selectableItems, searchQuery), [selectableItems, searchQuery]);

  // Single source of truth for item order. Both the hook (keyboard navigation)
  // and renderOptions (DOM rendering) consume this list — no independent sorting.
  // Selected-at-open items are placed first within each group/section, and the
  // same walk applies while searching so group structure survives filtering
  // (only matching items are kept; the query is empty in non-search mode).
  const sortedItems = useMemo(() => {
    const selectedSet = selectedAtOpen ?? new Set();
    const result = [];
    let pendingFlat = [];
    const orderSelectedFirst = items => {
      const selected = items.filter(item => selectedSet.has(item.value));
      const unselected = items.filter(item => !selectedSet.has(item.value));
      return [...selected, ...unselected];
    };
    const flushFlat = () => {
      if (pendingFlat.length === 0) {
        return;
      }
      result.push(...orderSelectedFirst(pendingFlat));
      pendingFlat = [];
    };
    for (const option of options) {
      if (isDivider(option)) {
        flushFlat();
      } else if (isSection(option)) {
        flushFlat();
        const sectionOptions = option.options.map(opt => normalizeOption(opt)).filter(opt => optionMatchesQuery(opt, searchQuery));
        result.push(...orderSelectedFirst(sectionOptions));
      } else if (isOptionData(option)) {
        const normalized = normalizeOption(option);
        if (optionMatchesQuery(normalized, searchQuery)) {
          pendingFlat.push(normalized);
        }
      }
    }
    flushFlat();
    if (hasSelectAll) {
      return [{
        value: SELECT_ALL_VALUE,
        label: selectAllLabel
      }, ...result];
    }
    return result;
  }, [searchQuery, options, selectedAtOpen, hasSelectAll, selectAllLabel]);

  // Layer for dropdown positioning
  const handleLayerHide = useCallback(() => {
    setSearchQuery('');
    setSelectedAtOpen(null);
    // Clear any lingering result count when the popover closes so stale status
    // text does not linger in the a11y tree.
    announce('');
    triggerRef.current?.focus();
  }, [announce]);
  const popover = usePopover({
    hasLightDismiss: true,
    onHide: handleLayerHide,
    hasCloseButton: false,
    hasAutoFocus: false,
    // The popup's own role="listbox" is the exposed semantics; the trigger
    // keeps DOM focus, so wrapping it in a modal dialog would misrepresent it.
    role: 'none'
  });

  // Open dropdown on mount when isDefaultOpen is true
  useEffect(() => {
    if (isDefaultOpen) {
      popover.show();
    }
    // eslint-disable-next-line @eslint-react/exhaustive-deps -- mount-only: isDefaultOpen is not reactive
  }, []);

  // Announce the filtered result count from the query-change handler (matching
  // BaseTypeahead) rather than a reactive effect: computing the count for the
  // next query here fires the announcement exactly once per keystroke and does
  // not re-speak on unrelated re-renders. Reuses the announce instance shared
  // with the selection-count announcements above.
  const handleSearchChange = useCallback(nextQuery => {
    setSearchQuery(nextQuery);
    if (nextQuery.length === 0) {
      // Emptying the query clears the region rather than announcing a count.
      announce('');
      return;
    }
    const count = filterOptionsByQuery(selectableItems, nextQuery).length;
    announce(count === 0 ? 'No results found' : `${count} result${count === 1 ? '' : 's'}`);
  }, [announce, selectableItems]);

  // Handle toggle
  // Clear all selected values. Shared by the clear button and the keyboard
  // Delete/Backspace path so clearing is reachable without a mouse.
  const clearValues = useCallback(() => {
    onChange([]);
    announceSelection([]);
    if (changeAction) {
      startTransition(async () => {
        setOptimisticValue([]);
        await changeAction([]);
      });
    }
  }, [onChange, changeAction, startTransition, setOptimisticValue, announceSelection]);

  // Whether there is at least one selected value (clearing is meaningful).
  const hasValue = optimisticValue.length > 0;
  const handleClear = useCallback(e => {
    e.stopPropagation(); // Don't open dropdown
    clearValues();
  }, [clearValues]);
  const handleToggle = useCallback(itemValue => {
    const newValue = optimisticValue.includes(itemValue) ? optimisticValue.filter(v => v !== itemValue) : [...optimisticValue, itemValue];
    onChange(newValue);
    announceSelection(newValue);
    if (changeAction) {
      startTransition(async () => {
        setOptimisticValue(newValue);
        await changeAction(newValue);
      });
    }
  }, [optimisticValue, onChange, changeAction, startTransition, setOptimisticValue, announceSelection]);

  // Select-all logic
  const enabledItems = useMemo(() => filteredItems.filter(item => !item.disabled), [filteredItems]);
  const allEnabledSelected = useMemo(() => enabledItems.length > 0 && enabledItems.every(item => optimisticValue.includes(item.value)), [enabledItems, optimisticValue]);
  const someSelected = useMemo(() => enabledItems.some(item => optimisticValue.includes(item.value)), [enabledItems, optimisticValue]);
  const selectAllState = allEnabledSelected ? true : someSelected ? 'indeterminate' : false;
  const handleSelectAll = useCallback(() => {
    let newValue;
    if (allEnabledSelected) {
      // Deselect all enabled items, keep disabled items that are selected
      const enabledValues = new Set(enabledItems.map(item => item.value));
      newValue = optimisticValue.filter(v => !enabledValues.has(v));
    } else {
      // Select all enabled items
      const currentSet = new Set(optimisticValue);
      newValue = [...optimisticValue];
      for (const item of enabledItems) {
        if (!currentSet.has(item.value)) {
          newValue.push(item.value);
        }
      }
    }
    onChange(newValue);
    announceSelection(newValue);
    if (changeAction) {
      startTransition(async () => {
        setOptimisticValue(newValue);
        await changeAction(newValue);
      });
    }
  }, [allEnabledSelected, enabledItems, optimisticValue, onChange, changeAction, startTransition, setOptimisticValue, announceSelection]);

  // Route toggle: select-all sentinel → handleSelectAll, everything else → handleToggle
  const handleNavigableToggle = useCallback(itemValue => {
    if (itemValue === SELECT_ALL_VALUE) {
      handleSelectAll();
    } else {
      handleToggle(itemValue);
    }
  }, [handleSelectAll, handleToggle]);

  // Multi-select combobox behavior — index-based, matching useCombobox pattern.
  // sortedItems is the single source of truth for item order.
  const {
    highlightedIndex,
    getItemId,
    onTriggerClick,
    onKeyDown,
    onItemMouseEnter
  } = useMultiCombobox({
    selectableItems: sortedItems,
    isDisabled,
    isOpen: popover.isOpen,
    hasSearch,
    onOpen: useCallback(() => {
      // Snapshot which items are selected at open time — sort is frozen until close
      setSelectedAtOpen(new Set(optimisticValue));
      popover.show();
      if (hasSearch) {
        // Focus search after popover opens
        requestAnimationFrame(() => {
          searchRef.current?.focus();
        });
      }
    }, [popover, hasSearch, optimisticValue]),
    onClose: popover.hide,
    onToggle: handleNavigableToggle,
    onClear: hasClear ? clearValues : undefined,
    hasValue,
    listboxId
  });

  // Keep the highlighted option visible during keyboard navigation. The
  // listbox is a fixed-height scroll container, so without this the virtual
  // cursor walks off-screen once navigation passes the visible window. Mirrors
  // CommandPaletteItem's scrollIntoView({block: 'nearest'}) behavior.
  useEffect(() => {
    if (!popover.isOpen || highlightedIndex < 0) {
      return;
    }
    document.getElementById(getItemId(highlightedIndex))?.scrollIntoView?.({
      block: 'nearest'
    });
  }, [popover.isOpen, highlightedIndex, getItemId]);

  // Build trigger display content
  const selectedLabels = useMemo(() => {
    return optimisticValue.map(v => {
      const item = selectableItems.find(i => i.value === v);
      return item?.label ?? v;
    });
  }, [optimisticValue, selectableItems]);
  const renderTriggerContent = useCallback(() => {
    if (optimisticValue.length === 0) {
      return /*#__PURE__*/_jsx("span", {
        ...{
          className: "xb3r6kr xlyipyv xuxw1ft"
        },
        children: placeholder
      });
    }
    switch (triggerDisplay) {
      case 'count':
        return /*#__PURE__*/_jsxs("span", {
          ...{
            className: "xb3r6kr xlyipyv xuxw1ft"
          },
          children: [optimisticValue.length, " selected"]
        });
      case 'labels':
        {
          const displayed = selectedLabels.slice(0, 3);
          const remaining = selectedLabels.length - displayed.length;
          const text = remaining > 0 ? `${displayed.join(', ')}, +${remaining}` : displayed.join(', ');
          return /*#__PURE__*/_jsx("span", {
            ...{
              className: "xb3r6kr xlyipyv xuxw1ft"
            },
            children: text
          });
        }
      case 'badges':
        {
          const displayed = selectedLabels.slice(0, maxBadges);
          const remaining = selectedLabels.length - displayed.length;
          return /*#__PURE__*/_jsxs("span", {
            ...{
              className: "x78zum5 x1a02dak xzye2dw x6s0dn4"
            },
            children: [displayed.map(label => /*#__PURE__*/_jsx(Badge, {
              label: label,
              variant: "neutral"
            }, label)), remaining > 0 && /*#__PURE__*/_jsxs("span", {
              ...{
                className: "x2lah0s xcr08ib xv1l7n4 x1e4wzip"
              },
              children: ["+", remaining]
            })]
          });
        }
    }
  }, [optimisticValue, triggerDisplay, selectedLabels, placeholder, maxBadges]);

  // Render search input
  const renderSearch = useCallback(() => {
    if (!hasSearch) {
      return null;
    }
    return /*#__PURE__*/_jsx("div", {
      ...{
        className: "x78zum5 x6s0dn4 xf314gf xu0wf1k"
      },
      onKeyDown: e => {
        // The clear (✕) button lives inside the TextInput, after the input in
        // DOM order. When it is focused and the user tabs forward there is
        // nothing else in the popup, so dismiss it (Shift+Tab returns to the
        // input natively). Key events originating on the input are handled on
        // the input below; ignore them here so we don't double-dismiss.
        if (e.target === searchRef.current) {
          return;
        }
        if (e.key === 'Tab' && !e.shiftKey) {
          onKeyDown(e);
        }
      },
      children: /*#__PURE__*/_jsx(TextInput, {
        ref: searchRef,
        id: searchId
        // The search field IS a TextInput: the leading magnifier is its
        // `startIcon` and the trailing clear (✕) is its built-in `hasClear`
        // (which resets the value and refocuses the input). We add no bespoke
        // affordance chrome — the field just looks and behaves like every
        // other Astryx input.
        ,
        label: t('@astryx.multiSelector.searchOptions'),
        isLabelHidden: true,
        startIcon: "search",
        hasClear: true,
        size: "sm"
        // Fill the dropdown's width (minus the wrapper's inline padding) so
        // the field is flush end-to-end rather than sized to its content.
        ,
        width: "100%"
        // When hasSearch is set, focus moves into this input on open, so it —
        // not the trigger — must be the combobox reporting the highlighted
        // option via aria-activedescendant (comboboxes-4). role + aria-* pass
        // through to the underlying <input> via BaseProps.
        ,
        role: "combobox",
        "aria-expanded": popover.isOpen,
        "aria-controls": listboxId,
        "aria-autocomplete": "list",
        "aria-activedescendant": popover.isOpen && highlightedIndex >= 0 ? getItemId(highlightedIndex) : undefined,
        value: searchQuery,
        onChange: handleSearchChange,
        onKeyDown: e => {
          // Arrow keys navigate options; Enter toggles; Escape closes.
          // Space and Home/End are left to the input (type a space / move
          // the caret) per the APG editable combobox; PageUp/PageDown are
          // the sanctioned substitute for jumping to the first/last option.
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'PageUp' || e.key === 'PageDown' || e.key === 'Enter' || e.key === 'Escape') {
            onKeyDown(e);
            return;
          }
          // Tab: when a query is showing the clear (✕) button, forward-tab
          // moves focus to it (keeping the popup open) so the affordance is
          // keyboard-reachable. Every other Tab dismisses the popup as usual.
          if (e.key === 'Tab' && (e.shiftKey || !hasQuery)) {
            onKeyDown(e);
          }
        },
        placeholder: searchPlaceholder
      })
    });
  }, [hasSearch, searchId, listboxId, searchQuery, hasQuery, searchPlaceholder, handleSearchChange, onKeyDown, popover.isOpen, highlightedIndex, getItemId, t]);

  // Render an individual item (index-based)
  const renderItem = useCallback((item, flatIndex) => {
    const isHighlighted = flatIndex === highlightedIndex;
    const isSelectAll = item.value === SELECT_ALL_VALUE;
    const isSelected = isSelectAll ? allEnabledSelected : optimisticValue.includes(item.value);
    const checkboxValue = isSelectAll ? selectAllState : isSelected;
    // aria-selected="mixed" is invalid on role="option", and the tri-state
    // checkbox is inert/decorative, so the indeterminate state must be
    // conveyed through the option's accessible name (WCAG 4.1.2).
    const isPartiallySelected = isSelectAll && selectAllState === 'indeterminate';
    return /*#__PURE__*/_jsxs("div", {
      id: getItemId(flatIndex),
      role: "option",
      "aria-selected": isSelected,
      "aria-label": isPartiallySelected ? t('@astryx.multiSelector.selectAllPartiallySelected', {
        label: selectAllLabel
      }) : undefined,
      "aria-disabled": item.disabled,
      onClick: () => {
        if (!item.disabled) {
          handleNavigableToggle(item.value);
        }
      },
      onMouseEnter: () => onItemMouseEnter(item, flatIndex),
      ...stylex.props(styles.item, isSelectAll ? selectAllSizeStyles[size] : itemSizeStyles[size], isSelectAll && styles.selectAllWrapper, isHighlighted && styles.itemHighlighted, item.disabled && styles.itemDisabled),
      children: [/*#__PURE__*/_jsx("div", {
        inert: true,
        ...{
          className: "x47corl x78zum5 x2lah0s"
        },
        children: /*#__PURE__*/_jsx(CheckboxInput, {
          label: "",
          isLabelHidden: true,
          value: checkboxValue,
          onChange: () => {},
          isDisabled: item.disabled,
          size: size === 'lg' ? 'md' : size
        })
      }), renderOption && !isSelectAll ? renderOption(item) : /*#__PURE__*/_jsx("span", {
        ...{
          0: {
            className: "x9ynric xcr08ib x1e4wzip x1tgivj0 xeuugli xb3r6kr xlyipyv xuxw1ft"
          },
          1: {
            className: "x9ynric xcr08ib x1e4wzip xeuugli xb3r6kr xlyipyv xuxw1ft xnbbluu"
          }
        }[!!item.disabled << 0],
        children: item.label ?? item.value
      })]
    }, item.value);
  }, [renderOption, highlightedIndex, optimisticValue, allEnabledSelected, selectAllState, selectAllLabel, getItemId, handleNavigableToggle, onItemMouseEnter, size, t]);

  // Render all options. Uses sortedItems as the single source of truth for
  // item order — no independent sorting here. Structural elements (dividers,
  // section headers) come from the original options prop.
  const renderOptions = useCallback(() => {
    const elements = [];
    let cursor = 0;

    // Number of real items (excluding the select-all sentinel)
    const realItemCount = hasSelectAll ? sortedItems.length - 1 : sortedItems.length;

    // Show select-all only when there are real items to select
    if (hasSelectAll && realItemCount > 0) {
      elements.push(renderItem(sortedItems[0], 0));
      elements.push(/*#__PURE__*/_jsx(Divider, {
        xstyle: styles.divider
      }, "select-all-divider"));
      cursor = 1;
    } else if (hasSelectAll) {
      // Skip the select-all sentinel when there are no real items
      cursor = 1;
    }

    // Empty state — no real items to show. role="presentation" keeps the
    // message out of the listbox's accessibility tree (role="listbox" only
    // permits option/group children); the no-results outcome is announced
    // via the result-count live region instead.
    if (realItemCount === 0) {
      elements.push(/*#__PURE__*/_jsx("div", {
        role: "presentation",
        ...mergeProps(themeProps('multi-selector-empty-state'), {
          className: "x1b2ylru x2b8uid xv1l7n4 x9ynric xcr08ib"
        }),
        children: "No results found"
      }, "empty"));
      return elements;
    }

    // Consume items from sortedItems in order, interleaving structural elements
    // (dividers, section headers) from the options prop. While searching, only
    // matching items are present in sortedItems, so a section consumes just its
    // matches and is skipped entirely when none match — no header left standing
    // over nothing, and the cursor stays aligned with the combobox indices.
    const isSearching = Boolean(searchQuery);
    let pendingCount = 0;
    const flushPending = () => {
      for (let j = 0; j < pendingCount; j++) {
        elements.push(renderItem(sortedItems[cursor], cursor));
        cursor++;
      }
      pendingCount = 0;
    };
    for (let i = 0; i < options.length; i++) {
      const option = options[i];
      if (isDivider(option)) {
        flushPending();
        // A standalone divider between groups would orphan itself once its
        // neighbors are filtered out, so skip it while searching.
        if (!isSearching) {
          elements.push(/*#__PURE__*/_jsx(Divider, {
            xstyle: styles.divider
          }, `divider-${i}`));
        }
      } else if (isSection(option)) {
        flushPending();
        const matchCount = isSearching ? option.options.filter(opt => optionMatchesQuery(normalizeOption(opt), searchQuery)).length : option.options.length;
        if (matchCount === 0) {
          continue;
        }
        const sectionItems = [];
        for (let j = 0; j < matchCount; j++) {
          sectionItems.push(renderItem(sortedItems[cursor], cursor));
          cursor++;
        }
        if (option.title) {
          elements.push(/*#__PURE__*/_jsx(Divider, {
            label: option.title,
            xstyle: styles.sectionDivider
          }, `section-divider-${i}`));
        }
        elements.push(/*#__PURE__*/_jsx("div", {
          role: "group",
          "aria-label": option.title,
          children: sectionItems
        }, `section-${i}`));
      } else if (isOptionData(option)) {
        if (!isSearching || optionMatchesQuery(normalizeOption(option), searchQuery)) {
          pendingCount++;
        }
      }
    }
    flushPending();
    return elements;
  }, [options, renderItem, sortedItems, searchQuery, hasSelectAll]);

  // The detached message box renders its own leading status icon, so the
  // on-field icon would duplicate it — keep the chevron indicator instead.
  const showStatusIcon = status != null && effectiveStatusVariant !== 'detached';
  const showStatusTooltip = status != null && effectiveStatusVariant === 'tooltip' && !!status.message;
  const multiSelectorContent = /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs("div", {
      ref: el => {
        popover.triggerRef(el);
        // Anchor + hover/focus listeners for the disabled-message tooltip.
        // Handlers are gated internally by isEnabled, and anchor names
        // compose, so attaching unconditionally is safe.
        disabledMessageTooltip.ref(el);
      },
      onClick: onTriggerClick,
      "data-testid": testId,
      ...mergeProps(themeProps('multi-selector', {
        variant,
        size,
        status: status?.type ?? null
      }), stylex.props(inputWrapperStyles.base, styles.triggerContainer, sizeStyles[size], variant === 'ghost' && styles.triggerGhost, isDisabled && inputWrapperStyles.disabled, variant === 'ghost' && isDisabled && styles.triggerGhostDisabled, optimisticValue.length === 0 && styles.triggerPlaceholder, variant !== 'ghost' && status && inputStatusBorderStyles[status.type], variant !== 'ghost' && status && !isDisabled && inputStatusHoverShadowStyles[status.type], variant !== 'ghost' && inputGroup && groupStyles.inGroup, xstyle), className, style),
      children: [startIcon && renderIconSlot(startIcon, {
        size: 'sm',
        color: 'secondary'
      }), inputGroup && /*#__PURE__*/_jsx(VisuallyHidden, {
        id: inputLabelId,
        children: label
      }), /*#__PURE__*/_jsx("button", {
        ref: triggerRef,
        id: triggerId,
        type: "button"
        // In hasSearch mode the popup's search input is the combobox (it owns
        // focus + aria-activedescendant, comboboxes-4), so the trigger is a
        // plain button that opens the listbox — not a second combobox.
        ,
        role: hasSearch ? undefined : 'combobox',
        "aria-haspopup": "listbox",
        "aria-expanded": popover.isOpen,
        "aria-controls": listboxId,
        "aria-activedescendant": !hasSearch && popover.isOpen && highlightedIndex >= 0 ? getItemId(highlightedIndex) : undefined,
        "aria-describedby": ariaDescribedBy,
        "aria-labelledby": ariaLabelledBy,
        "aria-required": isRequired ? 'true' : undefined,
        "aria-invalid": status?.type === 'error' ? 'true' : undefined,
        "aria-busy": isBusy || undefined
        // With a disabledMessage the trigger keeps focusability via
        // aria-disabled so the reason is focus-discoverable; activation is
        // still blocked by the isDisabled guards in useMultiCombobox.
        ,
        disabled: isDisabled && !showsDisabledMessage,
        "aria-disabled": showsDisabledMessage ? 'true' : undefined,
        onKeyDown: onKeyDown,
        tabIndex: isDisabled && !showsDisabledMessage ? -1 : 0,
        ...{
          className: "x78zum5 x6s0dn4 x1qughib x1txdalj x1iyjqo2 xs83m0k x1r8uery xeuugli x1717udv x1ghz6dp xc342km xng3xce xjbqb8w xjb2p0i x1qlqyl8 x15bjb6t x1heor9g x1ypdohk x1a2a7pz xh6dtrn"
        },
        children: /*#__PURE__*/_jsx("span", {
          ...{
            className: "x78zum5 x6s0dn4 xzye2dw x1iyjqo2 xs83m0k x1r8uery xeuugli xb3r6kr"
          },
          children: renderTriggerContent()
        })
      }), htmlName != null && value.map(v => /*#__PURE__*/_jsx("input", {
        type: "hidden",
        name: htmlName,
        value: v
        // Disabled native controls are excluded from form submission;
        // mirror that for the hidden carriers.
        ,
        disabled: isDisabled
      }, v)), isBusy && /*#__PURE__*/_jsx(Spinner, {
        size: "sm"
      }), hasClear && value.length > 0 && !isDisabled && /*#__PURE__*/_jsx("button", {
        type: "button",
        onClick: handleClear,
        "aria-label": t('@astryx.multiSelector.clearAll', {
          label
        }),
        ...{
          className: "x78zum5 x6s0dn4 xl56j7k x1717udv x1ghz6dp xc342km xng3xce xjbqb8w x1ypdohk xh6dtrn x1a2a7pz x1p25gnr x1y3gkto"
        },
        children: /*#__PURE__*/_jsx(Icon, {
          icon: "close",
          size: "sm",
          color: "secondary"
          // Stable theme target on the clear glyph itself, so a theme can
          // restyle just this icon (color, size, hover) via `defineTheme`.
          // Same-element rules in @layer astryx-theme win over the icon's
          // own base color/size, which a button-level target could not
          // reach.
          ,
          ...themeProps('multi-selector-clear-icon')
        })
      }), /*#__PURE__*/_jsx("span", {
        ...{
          0: {
            className: "x2lah0s x78zum5 x6s0dn4 xl56j7k x1kky2od xlup9mm x11xpdln xuedmi6 xlr8y92 x1g0ag68 xv9yike"
          },
          2: {
            className: "x2lah0s x78zum5 x6s0dn4 xl56j7k x1kky2od xlup9mm x11xpdln xuedmi6 xlr8y92 x1g0ag68 xv9yike x19jd1h0"
          },
          1: {
            className: "x2lah0s x78zum5 x6s0dn4 xl56j7k x1kky2od xlup9mm x11xpdln xuedmi6 xlr8y92 x1g0ag68 xv9yike xq2gx43"
          },
          3: {
            className: "x2lah0s x78zum5 x6s0dn4 xl56j7k x1kky2od xlup9mm x11xpdln xuedmi6 xlr8y92 x1g0ag68 xv9yike x19jd1h0 xq2gx43"
          }
        }[!!(!showStatusIcon && popover.isOpen) << 1 | !!showStatusIcon << 0],
        children: showStatusIcon ? showStatusTooltip ? /*#__PURE__*/_jsx("button", {
          ref: statusTooltip.ref,
          type: "button",
          "aria-label": t(STATUS_BUTTON_LABEL_KEY[status.type]),
          "aria-describedby": statusTooltip.describedBy,
          onClick: e => e.stopPropagation(),
          ...{
            className: "x78zum5 x6s0dn4 xl56j7k x1717udv x1ghz6dp xc342km xng3xce xjbqb8w x1heor9g x1ypdohk xh6dtrn x1a2a7pz x1p25gnr x1y3gkto"
          },
          children: /*#__PURE__*/_jsx(Icon, {
            icon: STATUS_ICON_MAP[status.type],
            size: "sm",
            color: STATUS_ICON_COLOR_MAP[status.type]
          })
        }) : /*#__PURE__*/_jsx(Icon, {
          icon: STATUS_ICON_MAP[status.type],
          size: "sm",
          color: STATUS_ICON_COLOR_MAP[status.type]
        }) : /*#__PURE__*/_jsx(Icon, {
          icon: "chevronDown",
          size: "sm",
          color: "inherit"
          // Stable theme target on the chevron glyph itself, so a theme can
          // restyle just this icon (color, size, hover) — and its
          // open/closed state — via `defineTheme`. Same-element rules in
          // @layer astryx-theme win over the icon's own base color/size,
          // which a button-level target could not reach.
          ,
          ...themeProps('multi-selector-indicator-icon', {
            state: popover.isOpen ? 'expanded' : 'collapsed'
          })
        })
      })]
    }), popover.render(/*#__PURE__*/_jsxs("div", {
      ...{
        className: "x9f619 xuyqlj2 x1odjw0f x9epnlk"
      },
      children: [renderSearch(), /*#__PURE__*/_jsx("div", {
        id: listboxId,
        role: "listbox",
        "aria-multiselectable": "true",
        "aria-labelledby": triggerId,
        children: renderOptions()
      })]
    }), {
      placement: 'below',
      alignment: 'start',
      xstyle: styles.popover
    }), showStatusTooltip && statusTooltip.renderTooltip(status?.message ?? ''), showsDisabledMessage && disabledMessageTooltip.renderTooltip(disabledMessage)]
  });
  if (inputGroup) {
    return multiSelectorContent;
  }
  return /*#__PURE__*/_jsx(Field, {
    label: label,
    isLabelHidden: isLabelHidden,
    description: description,
    inputID: triggerId,
    descriptionID: description ? descriptionId : undefined,
    isOptional: isOptional,
    isRequired: isRequired,
    isDisabled: isDisabled,
    status: status ? {
      type: status.type,
      message: status.message,
      messageID: status.message ? statusMessageId : undefined
    } : undefined,
    statusVariant: effectiveStatusVariant,
    labelTooltip: labelTooltip,
    width: width,
    children: multiSelectorContent
  });
}
MultiSelector.displayName = 'MultiSelector';